import { Component, ElementRef, OnInit, Inject,ViewChild} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, Brand, Category, Product, ProductAttribute } from '../auth.service';
import { CommonModule, DOCUMENT } from '@angular/common';
@Component({
  selector: 'app-product',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './product.component.html',
  styleUrl: './product.component.css'
})
export class ProductComponent implements OnInit {
  productForm: FormGroup;
  submitted = false;
  successMessage: string | null = null;
  errorMessage: string | null = null;
  products: Product[] = [];
  categories: Category[] = [];
  brands: Brand[] = [];
  userId!: number;
  selectedProduct: (Product & {
    gltfUrl: string | null;
    binUrl: string | null;
    textureUrl: string | null;
  }) | null = null;

  // Propriétés pour stocker les URLs des fichiers pour l'édition
  gltfUrl: string | null = null;
  binUrl: string | null = null;
  textureUrl: string | null = null;

  selectedFiles: { gltfPath?: File; binPath?: File; texturePath?: File } = {};
  isEditMode = false;
  editProductId: number | null = null;

  // Références aux éléments d'entrée de fichier
  @ViewChild('gltfInput') gltfInput!: ElementRef;
  @ViewChild('binInput') binInput!: ElementRef;
  @ViewChild('textureInput') textureInput!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private document:Document
  ) {
    this.productForm = this.fb.group({
      title: ['', Validators.required],
      slug: [''],
      specs: [''],
      status: [false, Validators.required],
      isFeatured: [false, Validators.required],
      debut: [''],
      fin: [''],
      categoryId: [null, Validators.required],
      brandId: [null, Validators.required],
      gltfUrl: [null],
      binUrl: [null],
      textureUrl: [null],
    });
  }

  ngOnInit(): void {
    this.userId = this.getCurrentUserId();
    this.loadProducts();
    this.loadCategories();
    this.loadBrands();
    
    this.route.paramMap.subscribe(params => {
      const idParam = params.get('id');
      if (idParam) {
        this.isEditMode = true;
        this.editProductId = +idParam;
        this.loadProduct(this.editProductId);
      }
    });
  }

  get f() {
    return this.productForm.controls;
  }

  private getCurrentUserId(): number {
    return 1; // Remplacez ceci par la logique réelle pour récupérer l'ID utilisateur
  }

  loadProducts(): void {
    this.authService.getProducts().subscribe(
      (data: Product[]) => {
        this.products = data;
      },
      error => this.handleError('Erreur lors du chargement des produits', error)
    );
  }

  loadProduct(id: number): void {
    this.authService.getProductById(id).subscribe(
      (data: Product) => {
        this.productForm.patchValue({
          title: data.title,
          slug: data.slug,
          specs: data.specs,
          status: data.status,
          isFeatured: data.isFeatured,
          debut: data.debut,
          fin: data.fin,
          categoryId: data.category?.id || null,
          brandId: data.brand?.id || null,
          gltfUrl: data.gltfPath ? this.authService.getImageUrl(data.gltfPath) : null,
          binUrl: data.binPath ? this.authService.getImageUrl(data.binPath) : null,
          textureUrl: data.texturePath ? this.authService.getImageUrl(data.texturePath) : null
        });
        // Mettre à jour les URLs des fichiers pour affichage
        this.gltfUrl = data.gltfPath ? this.authService.getImageUrl(data.gltfPath) : null;
        this.binUrl = data.binPath ? this.authService.getImageUrl(data.binPath) : null;
        this.textureUrl = data.texturePath ? this.authService.getImageUrl(data.texturePath) : null;
      },
      error => this.handleError('Erreur lors du chargement du produit', error)
    );
  }

  loadCategories(): void {
    this.authService.getCategories().subscribe(
      (data: Category[]) => (this.categories = data),
      error => this.handleError('Erreur lors du chargement des catégories', error)
    );
  }

  loadBrands(): void {
    this.authService.getBrand().subscribe(
      (data: Brand[]) => (this.brands = data),
      error => this.handleError('Erreur lors du chargement des marques', error)
    );
  }
  generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-+|-+$)/g, '');
  }

  // Méthode pour gérer les changements de fichiers
  onFileChange(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.selectedFiles = {
        ...this.selectedFiles,
        [controlName]: input.files[0]
      };
      this.productForm.patchValue({
        [controlName]: input.files[0]
      });
      this.productForm.get(controlName)?.updateValueAndValidity();

      // Optionnel : Mettre à jour l'URL de prévisualisation si nécessaire
      const reader = new FileReader();
      reader.onload = (e: any) => {
        if (controlName === 'gltfPath') {
          this.gltfUrl = e.target.result;
        } else if (controlName === 'binPath') {
          this.binUrl = e.target.result;
        } else if (controlName === 'texturePath') {
          this.textureUrl = e.target.result;
        }
      };
      reader.readAsDataURL(input.files[0]);
    }
  }

  // **Ajout de la méthode prepareEdit**
  prepareEdit(product: Product): void {
    this.isEditMode = true;
    this.editProductId = product.id ?? null;


    this.productForm.patchValue({
      title: product.title,
      slug: product.slug,
      specs: product.specs,
      status: product.status,
      isFeatured: product.isFeatured,
      debut: product.debut,
      fin: product.fin,
      categoryId: product.category?.id || null,
      brandId: product.brand?.id || null,
      gltfUrl: product.gltfPath ? this.getImageUrl(product.gltfPath) : null,
      binUrl: product.binPath ? this.getImageUrl(product.binPath) : null,
      textureUrl: product.texturePath ? this.getImageUrl(product.texturePath) : null
    });

    // Mettre à jour les URLs des fichiers pour affichage
    this.gltfUrl = product.gltfPath ? this.getImageUrl(product.gltfPath) : null;
    this.binUrl = product.binPath ? this.getImageUrl(product.binPath) : null;
    this.textureUrl = product.texturePath ? this.getImageUrl(product.texturePath) : null;
  }

  onSubmit(): void {
    this.submitted = true;
    this.successMessage = null;
    this.errorMessage = null;
    if (this.productForm.invalid) {
      console.error('Formulaire invalide:', this.productForm.errors);
      this.errorMessage = 'Veuillez corriger les erreurs dans le formulaire.';
      return;
    }

    const formValue = this.productForm.value;

    const product: Product = {
      userId: this.userId,
      title: formValue.title,
      slug: this.generateSlug(formValue.title),
      
      specs: formValue.specs,
      status: formValue.status,
      isFeatured: formValue.isFeatured,
      debut: formValue.debut,
      fin: formValue.fin,
      category: { id: formValue.categoryId } as Category,
      brand: { id: formValue.brandId } as Brand,
      // Ajoutez d'autres propriétés nécessaires ici
    };

    const files = this.selectedFiles;


    if (this.isEditMode && this.editProductId) {
      this.authService.updateProduct(this.editProductId, product, files).subscribe(
        () => {
          this.successMessage = 'Produit mis à jour avec succès!';
          this.resetForm();
          this.loadProducts();
        },
        error => {
          this.handleError('Échec de la mise à jour du produit', error);
        }
      );
    } else {
      this.authService.createProduct(product, files).subscribe(
        () => {
          this.successMessage = 'Produit créé avec succès!';
          this.resetForm();
          this.loadProducts();
        },
        error => {
          this.handleError('Échec de la création du produit', error);
        }
      );
    }
  }

  deleteProduct(id: number): void {
    if (confirm('Êtes-vous sûr de vouloir supprimer ce produit?')) {
      this.authService.deleteProduct(id).subscribe(
        () => {
          this.successMessage = 'Produit supprimé avec succès!';
          this.loadProducts();
        },
        error => this.handleError('Échec de la suppression du produit', error)
      );
    }
  }

  resetForm(): void {
    this.productForm.reset({
      title: '',
      slug: '',
      specs: '',
      status: false,
      isFeatured: false,
      debut: '',
      fin: '',
      categoryId: null,
      brandId: null,
      gltfUrl: null,
      binUrl: null,
      textureUrl: null,
    });
    this.selectedFiles = {};
    this.submitted = false;
    this.isEditMode = false;
    this.editProductId = null;

    // Réinitialiser les URLs des fichiers
    this.gltfUrl = null;
    this.binUrl = null;
    this.textureUrl = null;

    // Réinitialiser les éléments d'entrée de fichier dans le DOM
    if (this.gltfInput) {
      this.gltfInput.nativeElement.value = '';
    }
    if (this.binInput) {
      this.binInput.nativeElement.value = '';
    }
    if (this.textureInput) {
      this.textureInput.nativeElement.value = '';
    }
  }

  private handleError(message: string, error: any): void {
    this.errorMessage = message;
    console.error(message, error);
  }

  // Méthode d'assistance pour générer les URLs des images
  getImageUrl(filePath: string | undefined): string | null {
    return filePath ? this.authService.getImageUrl(filePath) : null;
  }


}
